import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, Minus, Loader2, RefreshCw, Search,
  Package, BarChart3, AlertTriangle, GitCompare, X, Check,
} from "lucide-react";

const fmtN = (n) => new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 }).format(Number(n) || 0);
const fmtN1 = (n) => new Intl.NumberFormat("it-IT", { maximumFractionDigits: 1 }).format(Number(n) || 0);

function TrendIcon({ pct }) {
  if (pct === null || pct === undefined) return <Minus className="w-3.5 h-3.5 text-zinc-500" />;
  if (pct > 5) return <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />;
  if (pct < -5) return <TrendingDown className="w-3.5 h-3.5 text-red-400" />;
  return <Minus className="w-3.5 h-3.5 text-zinc-500" />;
}

function MiniSparkline({ series, width = 120, height = 32 }) {
  if (!series || series.length < 2) return null;
  const vals = series.map((p) => p.qta_settimana);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const stepX = width / (vals.length - 1);
  const points = vals.map((v, i) => `${i * stepX},${height - ((v - min) / range) * height}`).join(" ");
  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline points={points} fill="none" stroke="rgb(252 211 77)" strokeWidth="1.5" />
      {vals.map((v, i) => (
        <circle key={i} cx={i * stepX} cy={height - ((v - min) / range) * height} r="1.5" fill="rgb(252 211 77)" />
      ))}
    </svg>
  );
}

export default function AdminForecast() {
  const { authAxios } = useAuth();
  const [items, setItems] = useState([]);
  const [macros, setMacros] = useState([]);
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(true);
  const [top, setTop] = useState(50);
  const [weeks, setWeeks] = useState(4);
  const [macro, setMacro] = useState("__all__");
  const [search, setSearch] = useState("");
  const [onlyAlerts, setOnlyAlerts] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Compare mode
  const [compareMode, setCompareMode] = useState(false);
  const [compareSkus, setCompareSkus] = useState([]); // array of sku
  const [compareData, setCompareData] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qsMacro = macro && macro !== "__all__" ? `&macro_category=${encodeURIComponent(macro)}` : "";
      const r = await authAxios.get(`/admin/forecast/articles?top=${top}&weeks=${weeks}${qsMacro}`);
      setItems(r.data.items || []);
      setMacros(r.data.macro_categories || []);
      setInfo(r.data.info || "");
    } catch (e) {
      toast.error("Errore caricamento previsioni");
    } finally {
      setLoading(false);
    }
  }, [authAxios, top, weeks, macro]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (sku) => {
    setDetail({ sku, loading: true });
    setDetailLoading(true);
    try {
      const r = await authAxios.get(`/admin/forecast/sku/${encodeURIComponent(sku)}?weeks=${weeks}`);
      setDetail({ ...r.data, loading: false });
    } catch (e) {
      toast.error("Errore caricamento dettaglio");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const toggleCompareSku = (sku) => {
    setCompareSkus((cur) => {
      if (cur.includes(sku)) return cur.filter((s) => s !== sku);
      if (cur.length >= 6) {
        toast.warning("Massimo 6 articoli a confronto");
        return cur;
      }
      return [...cur, sku];
    });
  };

  const runCompare = useCallback(async () => {
    if (compareSkus.length < 2) {
      toast.warning("Seleziona almeno 2 articoli per il confronto");
      return;
    }
    setCompareLoading(true);
    try {
      const r = await authAxios.get(`/admin/forecast/compare?skus=${compareSkus.join(",")}&weeks=${weeks}`);
      setCompareData(r.data);
    } catch (e) {
      toast.error("Errore caricamento confronto");
    } finally {
      setCompareLoading(false);
    }
  }, [authAxios, compareSkus, weeks]);

  const onRowClick = (sku) => {
    if (compareMode) toggleCompareSku(sku);
    else openDetail(sku);
  };

  const filtered = items.filter((it) => {
    if (onlyAlerts && !["esaurito", "basso", "attenzione"].includes(it.stock_alert)) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return it.sku.toLowerCase().includes(q) || (it.descr || "").toLowerCase().includes(q);
  });

  const totalForecast = filtered.reduce((a, b) => a + (b.forecast_next_week || 0), 0);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        <PageHeader title="Previsione Domanda" />

        {/* Filtri */}
        <Card className="bg-zinc-900/60 border-white/10 mb-4">
          <CardContent className="p-3 flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                placeholder="Cerca SKU o nome…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-zinc-950 border-white/10"
                data-testid="forecast-search"
              />
            </div>
            <Select value={String(weeks)} onValueChange={(v) => setWeeks(Number(v))}>
              <SelectTrigger className="w-36 bg-zinc-950 border-white/10" data-testid="forecast-weeks">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2">Media 2 sett.</SelectItem>
                <SelectItem value="4">Media 4 sett.</SelectItem>
                <SelectItem value="8">Media 8 sett.</SelectItem>
                <SelectItem value="12">Media 12 sett.</SelectItem>
              </SelectContent>
            </Select>
            <Select value={String(top)} onValueChange={(v) => setTop(Number(v))}>
              <SelectTrigger className="w-32 bg-zinc-950 border-white/10" data-testid="forecast-top">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="20">Top 20</SelectItem>
                <SelectItem value="50">Top 50</SelectItem>
                <SelectItem value="100">Top 100</SelectItem>
                <SelectItem value="200">Top 200</SelectItem>
              </SelectContent>
            </Select>
            {macros.length > 0 && (
              <Select value={macro} onValueChange={setMacro}>
                <SelectTrigger className="w-44 bg-zinc-950 border-white/10" data-testid="forecast-macro">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tutte le categorie</SelectItem>
                  {macros.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setCompareMode((v) => !v);
                if (compareMode) { setCompareSkus([]); setCompareData(null); }
              }}
              className={`border-white/10 ${compareMode ? "bg-cyan-500/10 text-cyan-300 border-cyan-500/30" : "text-zinc-400"}`}
              data-testid="forecast-compare-toggle"
            >
              <GitCompare className={`w-4 h-4 mr-1 ${compareMode ? "text-cyan-300" : "text-zinc-500"}`} />
              Confronto
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOnlyAlerts((v) => !v)}
              className={`border-white/10 ${onlyAlerts ? "bg-red-500/10 text-red-300 border-red-500/30" : "text-zinc-400"}`}
              data-testid="forecast-only-alerts-btn"
            >
              <AlertTriangle className={`w-4 h-4 mr-1 ${onlyAlerts ? "text-red-300" : "text-zinc-500"}`} />
              {onlyAlerts ? "Solo a rischio" : "Tutti"}
            </Button>
            <Button
              size="icon"
              variant="outline"
              className="border-white/10"
              onClick={load}
              disabled={loading}
              data-testid="forecast-reload"
              title="Ricarica"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </CardContent>
        </Card>

        {/* Sintesi */}
        {!loading && filtered.length > 0 && (
          <Card className="bg-zinc-900/60 border-amber-500/20 mb-4">
            <CardContent className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Articoli previsti</div>
                <div className="text-2xl font-bold text-amber-300">{filtered.length}</div>
              </div>
              <div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Quantità totale prevista</div>
                <div className="text-2xl font-bold text-amber-300">{fmtN(totalForecast)}</div>
              </div>
              <div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Finestra media</div>
                <div className="text-2xl font-bold text-amber-300">{weeks} sett.</div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lista */}
        {compareMode && (
          <Card className="bg-cyan-500/5 border-cyan-500/30 mb-3" data-testid="compare-bar">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <GitCompare className="w-4 h-4 text-cyan-300" />
                <span className="text-sm text-cyan-200 font-medium">Modalità confronto</span>
                <span className="text-[11px] text-zinc-400">
                  ({compareSkus.length}/6) Seleziona da 2 a 6 articoli cliccandoli, poi premi &quot;Confronta&quot;.
                </span>
                <div className="ml-auto flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCompareSkus([])}
                    className="border-white/10 h-7 text-xs"
                    disabled={compareSkus.length === 0}
                  >
                    Pulisci
                  </Button>
                  <Button
                    size="sm"
                    onClick={runCompare}
                    className="bg-cyan-500 hover:bg-cyan-600 text-white h-7 text-xs"
                    disabled={compareSkus.length < 2 || compareLoading}
                    data-testid="compare-run-btn"
                  >
                    {compareLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <BarChart3 className="w-3.5 h-3.5 mr-1" />}
                    Confronta
                  </Button>
                </div>
              </div>
              {compareSkus.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {compareSkus.map((sku) => {
                    const it = items.find((x) => x.sku === sku);
                    return (
                      <Badge
                        key={sku}
                        variant="outline"
                        className="border-cyan-500/40 text-cyan-200 bg-cyan-500/10 cursor-pointer hover:bg-cyan-500/20"
                        onClick={() => toggleCompareSku(sku)}
                      >
                        {sku} {it ? `· ${it.descr?.slice(0, 25) || ""}` : ""}
                        <X className="w-3 h-3 ml-1" />
                      </Badge>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="text-center py-16 text-zinc-500">
            <Loader2 className="w-6 h-6 inline animate-spin mr-2" /> Calcolo previsioni…
          </div>
        ) : info ? (
          <Card className="bg-zinc-900/60 border-white/10">
            <CardContent className="p-8 text-center text-zinc-400">{info}</CardContent>
          </Card>
        ) : filtered.length === 0 ? (
          <Card className="bg-zinc-900/60 border-white/10">
            <CardContent className="p-8 text-center text-zinc-500">Nessun articolo trovato.</CardContent>
          </Card>
        ) : (
          <div className="space-y-1.5" data-testid="forecast-list">
            {filtered.map((it, i) => (
              <Card
                key={it.sku}
                className={`bg-zinc-900/60 border-white/10 hover:border-amber-500/20 cursor-pointer transition-colors ${
                  compareMode && compareSkus.includes(it.sku) ? "border-cyan-500/40 bg-cyan-500/5" : ""
                }`}
                onClick={() => onRowClick(it.sku)}
                data-testid={`forecast-row-${i}`}
              >
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="text-xs font-mono text-zinc-500 w-8 shrink-0">
                    {compareMode ? (
                      compareSkus.includes(it.sku) ? (
                        <Check className="w-4 h-4 text-cyan-300" />
                      ) : (
                        <div className="w-4 h-4 rounded border border-zinc-700" />
                      )
                    ) : `#${i + 1}`}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px] text-zinc-400 border-white/10">{it.sku}</Badge>
                      <span className="font-medium text-white truncate">{it.descr || "—"}</span>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
                      <span>{it.n_periodi} periodi storici</span>
                      <span>·</span>
                      <span>media: {fmtN1(it.media_recente)}/sett</span>
                      <span>·</span>
                      <span>storico: {fmtN1(it.media_storica)}/sett</span>
                      {it.seasonal_factor && (
                        <>
                          <span>·</span>
                          <Badge variant="outline" className="text-[10px] border-fuchsia-500/30 text-fuchsia-300">
                            stagionalità {it.seasonal_factor}×
                          </Badge>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xl font-bold text-amber-300">{fmtN(it.forecast_next_week)}</div>
                    <div className="text-[10px] text-zinc-500">
                      {fmtN(it.conf_min)}–{fmtN(it.conf_max)}
                    </div>
                    {it.stock_alert && it.stock_alert !== "ok" && (
                      <Badge
                        variant="outline"
                        className={`text-[9px] mt-1 ${
                          it.stock_alert === "esaurito" ? "border-red-500/60 text-red-300 bg-red-500/15" :
                          it.stock_alert === "basso" ? "border-orange-500/40 text-orange-300 bg-orange-500/10" :
                          "border-amber-500/40 text-amber-300 bg-amber-500/10"
                        }`}
                      >
                        {it.stock_alert === "esaurito" ? "ESAURITO" :
                         it.stock_alert === "basso" ? "STOCK BASSO" : "ATTENZIONE"}
                        {it.giacenza_attuale !== null && it.giacenza_attuale !== undefined && (
                          <span className="ml-1">· {fmtN(it.giacenza_attuale)}</span>
                        )}
                      </Badge>
                    )}
                    {it.stock_alert === "ok" && it.giacenza_attuale !== null && (
                      <div className="text-[10px] text-emerald-400 mt-0.5">{fmtN(it.giacenza_attuale)} in stock</div>
                    )}
                    <div className="text-[10px] flex items-center justify-end gap-1 mt-0.5">
                      <TrendIcon pct={it.trend_pct} />
                      <span className={
                        it.trend_pct === null ? "text-zinc-500" :
                        it.trend_pct > 5 ? "text-emerald-400" :
                        it.trend_pct < -5 ? "text-red-400" : "text-zinc-500"
                      }>
                        {it.trend_pct === null ? "N/D" : `${it.trend_pct > 0 ? "+" : ""}${it.trend_pct}%`}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <p className="text-[11px] text-zinc-600 mt-4 text-center">
          Previsione = media mobile {weeks} settimane × (60% trend recente + 40% stagionalità se disponibile). Range = media ± 1.5σ.
        </p>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-2xl text-zinc-100">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Package className="w-5 h-5 text-amber-300" />
              {detail?.descr || detail?.product_name || "Dettaglio"}
              <Badge variant="outline" className="text-[10px] text-zinc-400 border-white/10">{detail?.sku}</Badge>
            </DialogTitle>
          </DialogHeader>
          {detailLoading || detail?.loading ? (
            <div className="text-center py-8 text-zinc-500">
              <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Caricamento…
            </div>
          ) : detail ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded bg-amber-500/10 border border-amber-500/30">
                  <div className="text-[10px] text-amber-200 uppercase tracking-wider">Previsione prossima sett.</div>
                  <div className="text-2xl font-bold text-amber-300">{fmtN(detail.forecast_next_week)} {detail.unit}</div>
                  <div className="text-[10px] text-zinc-400 mt-1">{fmtN(detail.conf_min)}–{fmtN(detail.conf_max)}</div>
                </div>
                <div className={`p-3 rounded border ${
                  detail.giacenza_attuale === null || detail.giacenza_attuale === undefined ? "bg-zinc-800/60 border-white/10" :
                  detail.giacenza_attuale <= 0 ? "bg-red-500/10 border-red-500/40" :
                  detail.giacenza_attuale < detail.forecast_next_week ? "bg-orange-500/10 border-orange-500/40" :
                  detail.giacenza_attuale < detail.forecast_next_week * 1.5 ? "bg-amber-500/10 border-amber-500/30" :
                  "bg-emerald-500/10 border-emerald-500/30"
                }`}>
                  <div className="text-[10px] text-zinc-400 uppercase tracking-wider">Giacenza attuale</div>
                  <div className={`text-2xl font-bold ${
                    detail.giacenza_attuale === null || detail.giacenza_attuale === undefined ? "text-zinc-500" :
                    detail.giacenza_attuale <= 0 ? "text-red-300" :
                    detail.giacenza_attuale < detail.forecast_next_week ? "text-orange-300" :
                    detail.giacenza_attuale < detail.forecast_next_week * 1.5 ? "text-amber-300" :
                    "text-emerald-300"
                  }`}>
                    {detail.giacenza_attuale !== null && detail.giacenza_attuale !== undefined ? fmtN(detail.giacenza_attuale) : "—"}
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-1">
                    {detail.giacenza_attuale !== null && detail.forecast_next_week > 0
                      ? `copertura ~${Math.round((detail.giacenza_attuale / detail.forecast_next_week) * 7)} gg`
                      : "(nessuna giacenza)"}
                  </div>
                </div>
                <div className="p-3 rounded bg-zinc-800/60 border border-white/10">
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Media recente</div>
                  <div className="text-xl font-bold text-emerald-300">{fmtN1(detail.media_recente)}</div>
                  <div className="text-[10px] text-zinc-500 mt-1">trend: {detail.trend_pct === null ? "—" : `${detail.trend_pct > 0 ? "+" : ""}${detail.trend_pct}%`}</div>
                </div>
              </div>
              {detail.series && detail.series.length > 1 && (
                <div className="p-3 rounded bg-zinc-800/40 border border-white/10">
                  <div className="flex items-center gap-2 mb-2 text-xs text-zinc-400">
                    <BarChart3 className="w-3.5 h-3.5" />
                    Storico settimanale ({detail.series.length} periodi)
                  </div>
                  <MiniSparkline series={detail.series} width={500} height={80} />
                  <div className="flex justify-between text-[10px] text-zinc-500 mt-1">
                    <span>{detail.series[0].date}</span>
                    <span>{detail.series[detail.series.length - 1].date}</span>
                  </div>
                </div>
              )}
              {detail.macro_category && (
                <div className="text-xs text-zinc-500">
                  Categoria: <span className="text-zinc-300">{detail.macro_category}</span>
                </div>
              )}
              {/* Trasparenza sulla stagionalità */}
              <div className="text-[11px] text-zinc-500 border-t border-white/5 pt-2 flex items-start gap-2">
                <span className="shrink-0 mt-0.5">Stagionalità:</span>
                {detail.seasonal_factor ? (
                  <span className="text-fuchsia-300">
                    {detail.seasonal_factor}× <span className="text-zinc-500">— affidabile (granularità mensile)</span>
                  </span>
                ) : (
                  <span className="text-zinc-400">
                    {detail.seasonal_reason === "low_granularity" && (
                      <>non disponibile — il periodo storico di un anno fa è troppo lungo (&gt; 35 gg). Importa dati mensili per attivarla.</>
                    )}
                    {detail.seasonal_reason === "no_year_ago" && (
                      <>non disponibile — manca un import vicino a un anno fa (±14 gg).</>
                    )}
                    {detail.seasonal_reason === "few_periods" && (
                      <>non disponibile — servono almeno 5 periodi storici (attuali: {detail.n_periodi}).</>
                    )}
                    {detail.seasonal_reason === "no_baseline" && (
                      <>non calcolabile — base storica nulla.</>
                    )}
                    {(!detail.seasonal_reason || detail.seasonal_reason === "ok") && (
                      <>non disponibile.</>
                    )}
                  </span>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Compare dialog */}
      <Dialog open={!!compareData} onOpenChange={(o) => !o && setCompareData(null)}>
        <DialogContent className="bg-zinc-900 border-cyan-500/30 max-w-3xl text-zinc-100">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <GitCompare className="w-5 h-5 text-cyan-300" />
              Confronto storico ({compareData?.items?.length || 0} articoli)
            </DialogTitle>
          </DialogHeader>
          {compareData && compareData.items && compareData.items.length > 0 && (
            <CompareChart data={compareData.items} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===== CompareChart: SVG sovrapposto multi-serie =====
function CompareChart({ data }) {
  const colors = ["#fbbf24", "#22d3ee", "#a78bfa", "#34d399", "#f472b6", "#fb923c"];
  // Union of all dates from all series, sorted
  const allDates = Array.from(
    new Set(data.flatMap((s) => s.series.map((p) => p.date)))
  ).sort();
  const allVals = data.flatMap((s) => s.series.map((p) => p.qta_settimana));
  const max = Math.max(...allVals, 1);
  const min = 0; // 0-baseline più leggibile
  const W = 700, H = 220, PADL = 50, PADB = 28, PADT = 12, PADR = 12;
  const innerW = W - PADL - PADR;
  const innerH = H - PADT - PADB;
  const xFor = (date) => {
    const idx = allDates.indexOf(date);
    if (idx < 0) return PADL;
    return PADL + (idx / Math.max(allDates.length - 1, 1)) * innerW;
  };
  const yFor = (v) => PADT + innerH - ((v - min) / (max - min)) * innerH;

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 600 }}>
          {/* gridlines */}
          {[0, 0.25, 0.5, 0.75, 1].map((r, i) => (
            <g key={i}>
              <line
                x1={PADL}
                y1={PADT + innerH * r}
                x2={W - PADR}
                y2={PADT + innerH * r}
                stroke="rgba(255,255,255,0.08)"
                strokeDasharray="2,3"
              />
              <text
                x={PADL - 6}
                y={PADT + innerH * r + 3}
                fontSize="9"
                textAnchor="end"
                fill="rgba(255,255,255,0.4)"
              >
                {fmtAxis(max - (max - min) * r)}
              </text>
            </g>
          ))}
          {/* x axis labels — first / last only */}
          {allDates.length > 0 && (
            <>
              <text x={PADL} y={H - 6} fontSize="9" fill="rgba(255,255,255,0.4)">{allDates[0]}</text>
              <text x={W - PADR} y={H - 6} fontSize="9" textAnchor="end" fill="rgba(255,255,255,0.4)">
                {allDates[allDates.length - 1]}
              </text>
            </>
          )}
          {/* one polyline per series */}
          {data.map((s, idx) => {
            const color = colors[idx % colors.length];
            const points = s.series.map((p) => `${xFor(p.date)},${yFor(p.qta_settimana)}`).join(" ");
            return (
              <g key={s.sku}>
                <polyline points={points} fill="none" stroke={color} strokeWidth="1.8" />
                {s.series.map((p, i) => (
                  <circle key={i} cx={xFor(p.date)} cy={yFor(p.qta_settimana)} r="2" fill={color} />
                ))}
              </g>
            );
          })}
        </svg>
      </div>
      {/* Legend + KPI */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
        {data.map((s, idx) => (
          <div key={s.sku} className="flex items-center gap-2 p-2 rounded bg-zinc-800/40 border border-white/10">
            <span
              className="inline-block w-3 h-3 rounded-full shrink-0"
              style={{ backgroundColor: colors[idx % colors.length] }}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <Badge variant="outline" className="text-[10px] text-zinc-400 border-white/10">{s.sku}</Badge>
                <span className="text-zinc-100 truncate">{s.descr}</span>
              </div>
              <div className="text-[10px] text-zinc-400 mt-0.5">
                Previsione: <span className="text-amber-300 font-semibold">{fmtAxis(s.forecast_next_week)}</span>
                {" · "}media: {fmtAxis(s.media_recente)}
                {s.trend_pct !== null && (
                  <> · trend: <span className={s.trend_pct > 5 ? "text-emerald-400" : s.trend_pct < -5 ? "text-red-400" : "text-zinc-400"}>
                    {s.trend_pct > 0 ? "+" : ""}{s.trend_pct}%
                  </span></>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function fmtAxis(v) {
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
  return Math.round(v).toString();
}
